/*
 * Live knowledge-base search.
 *
 * Progressive enhancement for any `<form data-live-search>` that wraps an
 * `input[name="q"]` and a `[data-live-results]` panel. As the user types it
 * queries the JSON endpoint (`data-api`, default /api/search/) and shows the
 * top hits in a dropdown; the form still GET-submits to its `action` (the full
 * results page) on Enter or via "View all results", so it works without JS too.
 *
 * Result text is inserted with textContent (never innerHTML) so titles can't
 * inject markup.
 */
(function () {
    var MIN_CHARS = 2;
    var MAX_ITEMS = 8;
    var DEBOUNCE_MS = 220;

    function initLiveSearch(form) {
        var input = form.querySelector('input[name="q"]');
        var panel = form.querySelector('[data-live-results]');
        if (!input || !panel) return;

        var apiUrl = form.getAttribute('data-api') || '/api/search/';
        var resultsUrl = form.getAttribute('action') || '/support/search/';
        // Scope inputs may be a hidden field or a <select> (the support finder),
        // so match by name, not tag.
        var typeInput = form.querySelector('[name="type"]');
        var productInput = form.querySelector('[name="product"]');
        var containerInput = form.querySelector('[name="container"]');
        var clearBtn = form.querySelector('.ls-search-clear');
        // Visually-hidden aria-live region: announces the suggestion count so the
        // combobox is usable by screen readers, not just sighted pointer users.
        var statusRegion = form.querySelector('[data-live-status]');
        var optIdBase = (panel.id || 'ls-live') + '-opt';
        // A non-empty initial value means the page was server-rendered for a
        // query, so clearing must reload to drop those results, not just empty
        // the box. (Avoids sniffing the URL.)
        var hadServerResults = !!input.value;

        var timer = null;
        var lastQuery = null;
        var items = [];
        var active = -1;

        function currentType() {
            return typeInput && typeInput.value ? typeInput.value : '';
        }

        function currentProduct() {
            return productInput && productInput.value ? productInput.value : '';
        }

        function currentContainer() {
            return containerInput && containerInput.value ? containerInput.value : '';
        }

        function buildUrl(base, query) {
            var url = base + '?q=' + encodeURIComponent(query);
            var type = currentType();
            if (type) url += '&type=' + encodeURIComponent(type);
            var product = currentProduct();
            if (product) url += '&product=' + encodeURIComponent(product);
            var container = currentContainer();
            if (container) url += '&container=' + encodeURIComponent(container);
            return url;
        }

        function announce(msg) {
            if (statusRegion) statusRegion.textContent = msg;
        }

        function open() {
            panel.hidden = false;
            input.setAttribute('aria-expanded', 'true');
        }

        function close() {
            panel.hidden = true;
            panel.textContent = '';
            items = [];
            active = -1;
            input.setAttribute('aria-expanded', 'false');
            input.removeAttribute('aria-activedescendant');
        }

        function highlight() {
            items.forEach(function (el, i) {
                var on = i === active;
                el.classList.toggle('is-active', on);
                el.setAttribute('aria-selected', on ? 'true' : 'false');
            });
            if (active >= 0) {
                input.setAttribute('aria-activedescendant', items[active].id);
                items[active].scrollIntoView({ block: 'nearest' });
            } else {
                input.removeAttribute('aria-activedescendant');
            }
        }

        // Register a rendered anchor as a listbox option: role, initial selected
        // state and a stable id (for aria-activedescendant), tracked in `items`
        // for keyboard nav. Called at creation, so there's no second DOM pass.
        function addOption(a) {
            a.id = optIdBase + items.length;
            a.setAttribute('role', 'option');
            a.setAttribute('aria-selected', 'false');
            items.push(a);
        }

        function render(query, results) {
            panel.textContent = '';
            var top = results.slice(0, MAX_ITEMS);

            if (!top.length) {
                var none = document.createElement('div');
                none.className = 'ls-live-empty';
                none.textContent = 'No matches. Press Enter to search.';
                none.setAttribute('role', 'presentation');
                panel.appendChild(none);
                open();
                items = [];
                active = -1;
                announce('No matches. Press Enter to search all results.');
                return;
            }

            items = [];
            var list = document.createElement('ul');
            list.className = 'ls-live-list';
            list.setAttribute('role', 'presentation');
            top.forEach(function (r) {
                // Skip a result with no URL: `a.href = null` would coerce to the
                // string "null" and resolve as a RELATIVE link, sending the user
                // to <current-page>/null (a 404). The API always sends a real URL
                // today; this just keeps a future change from leaking /null.
                if (!r.url) return;
                var li = document.createElement('li');
                li.setAttribute('role', 'presentation');
                var a = document.createElement('a');
                a.className = 'ls-live-item';
                a.href = r.url;
                addOption(a);

                var kind = document.createElement('span');
                kind.className = 'ls-live-kind kind-text-' + (r.kind_css || '');
                kind.textContent = r.kind_display;

                var title = document.createElement('span');
                title.className = 'ls-live-title';
                title.textContent = r.title;

                a.appendChild(kind);
                a.appendChild(title);
                if (r.product_name) {
                    var prod = document.createElement('span');
                    prod.className = 'ls-live-product';
                    prod.textContent = r.product_name;
                    a.appendChild(prod);
                }
                // Parent container (e.g. a help page's handbook/topic), when present.
                if (r.container_name) {
                    var section = document.createElement('span');
                    section.className = 'ls-live-section';
                    section.textContent = r.container_name;
                    a.appendChild(section);
                }
                li.appendChild(a);
                list.appendChild(li);
            });
            panel.appendChild(list);

            var all = document.createElement('a');
            all.className = 'ls-live-all';
            all.href = buildUrl(resultsUrl, query);
            all.textContent = 'View all results for “' + query + '”';
            addOption(all);
            panel.appendChild(all);

            open();
            active = -1;
            announce(top.length + (top.length === 1 ? ' suggestion' : ' suggestions') + ' available. Use the up and down arrow keys to review, Enter to open.');
        }

        function run() {
            var query = input.value.trim();
            if (query.length < MIN_CHARS) {
                lastQuery = null;
                close();
                return;
            }
            if (query === lastQuery) return;
            lastQuery = query;
            // Only fetch the handful of rows the dropdown shows.
            fetch(buildUrl(apiUrl, query) + '&limit=' + MAX_ITEMS, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
                .then(function (r) { return r.ok ? r.json() : null; })
                .then(function (data) {
                    // Ignore stale responses if the box changed while in flight.
                    if (data && input.value.trim() === query) render(query, data.results || []);
                })
                .catch(function () { /* network hiccup: silently fall back to Enter */ });
        }

        input.addEventListener('input', function () {
            clearTimeout(timer);
            timer = setTimeout(run, DEBOUNCE_MS);
        });

        input.addEventListener('focus', function () {
            if (items.length) open();
        });

        input.addEventListener('keydown', function (e) {
            if (panel.hidden || !items.length) return;
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                active = Math.min(active + 1, items.length - 1);
                highlight();
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                active = Math.max(active - 1, 0);
                highlight();
            } else if (e.key === 'Enter') {
                if (active >= 0) { e.preventDefault(); items[active].click(); }
            } else if (e.key === 'Escape') {
                close();
            }
        });

        lsWireSearchClear(input, clearBtn, function () {
            lastQuery = null;
            close();
            // If results were server-rendered for a query, emptying the box
            // alone leaves them on screen - reload the bare page to clear them.
            if (hadServerResults) {
                window.location.assign(resultsUrl);
            } else {
                input.focus();
            }
        });

        document.addEventListener('click', function (e) {
            if (!form.contains(e.target)) close();
        });
    }

    document.querySelectorAll('form[data-live-search]').forEach(initLiveSearch);
})();
